import { defineStore } from 'pinia'

import type { DateRangeBounds, DateRangeSelection } from './types'

export const DATE_RANGE_PERSIST_KEY = 'orphex.v4.date-range.v1'
export const DATE_RANGE_MAX_AGE_MS = 24 * 60 * 60 * 1000

interface DateRangeState {
  selections: Record<string, DateRangeSelection>
  bounds: Record<string, DateRangeBounds>
}

const emptyBounds = (): DateRangeBounds => ({ min: null, max: null })

export const useDateRangeStore = defineStore('dateRangeStore', {
  state: (): DateRangeState => ({ selections: {}, bounds: {} }),
  getters: {
    selectionFor: (state) => (id: string) => state.selections[id] ?? null,
    boundsFor: (state) => (id: string) => state.bounds[id] ?? emptyBounds(),
    isStale:
      (state) =>
      (id: string, maxAgeMs = DATE_RANGE_MAX_AGE_MS) => {
        const savedAt = state.selections[id]?.savedAt
        if (!savedAt) return false
        const savedTime = Date.parse(savedAt)
        return Number.isNaN(savedTime) || Date.now() - savedTime >= maxAgeMs
      },
  },
  actions: {
    setSelection(id: string, selection: DateRangeSelection, linkedIds: readonly string[] = []) {
      for (const key of new Set([id, ...linkedIds].filter(Boolean))) {
        this.selections[key] = { ...selection }
      }
    },
    setBounds(id: string, bounds: DateRangeBounds) {
      this.bounds[id] = { ...bounds }
    },
    clear(id: string) {
      delete this.selections[id]
      delete this.bounds[id]
    },
    resetState() {
      this.selections = {}
      this.bounds = {}
    },
  },
  persist: { key: DATE_RANGE_PERSIST_KEY, pick: ['selections'] },
})
